import { Coach, LegacyCoach, CoachPerformance, ExtendedCoachPerformance, UpdateCoachData, LegacyCoachPerformance } from '../types';

/**
 * Transform backend Coach data to frontend LegacyCoach format
 * Used for compatibility with existing components
 */
export function transformCoachToLegacy(backendCoach: Coach): LegacyCoach {
  const user = backendCoach.user;
  const assignedClientsCount = backendCoach.assignedClients?.length || 0;
  
  return {
    id: backendCoach.id,
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phoneNumber || '',
    
    // Handle specializations and certifications as arrays (backend stores them as arrays)
    specializations: Array.isArray(backendCoach.specializations) 
      ? backendCoach.specializations 
      : backendCoach.specializations 
        ? [backendCoach.specializations] 
        : [],
    certifications: Array.isArray(backendCoach.certifications)
      ? backendCoach.certifications
      : backendCoach.certifications
        ? [backendCoach.certifications]
        : [],
    
    assignedClients: assignedClientsCount,
    onboardingProgress: 100, // Assume completed for existing coaches
    status: backendCoach.isActive ? 'Active' : 'Inactive',
    startDate: backendCoach.createdAt ? new Date(backendCoach.createdAt).toISOString().split('T')[0] : '',
    salary: backendCoach.hourlyRate ? Math.round(backendCoach.hourlyRate * 2080) : 0, // Convert hourly to annual
    notes: backendCoach.bio || '',
    warnings: 0,
    errors: 0,
    performanceRating: backendCoach.performanceMetrics?.avgRating || 0,
    clientSatisfactionScore: backendCoach.performanceMetrics?.clientRetention || 0,
    retentionRate: backendCoach.performanceMetrics?.clientRetention || 0,
    onboardingChecklist: {
      contractSigned: backendCoach.isActive,
      accountRegistered: true,
      trainerizeAdded: assignedClientsCount > 0,
      courseComplete: (backendCoach.specializations?.length || 0) > 0,
      clientsAssigned: assignedClientsCount > 0,
    },
    address: '',
    emergencyContact: '',
    lastActivity: backendCoach.updatedAt ? String(backendCoach.updatedAt) : String(backendCoach.createdAt) || '',
  };
}

/**
 * Transform frontend LegacyCoach data to backend Coach creation format
 */
export function transformLegacyToCreateData(legacyCoach: Partial<LegacyCoach>) {
  return {
    firstName: legacyCoach.firstName || '',
    lastName: legacyCoach.lastName || '',
    email: legacyCoach.email || '',
    phoneNumber: legacyCoach.phone || '', // Add phone number for user creation
    // CREATE expects arrays - keep as arrays for creation
    specializations: Array.isArray(legacyCoach.specializations) 
      ? legacyCoach.specializations 
      : legacyCoach.specializations 
        ? [legacyCoach.specializations] 
        : [],
    certifications: Array.isArray(legacyCoach.certifications) 
      ? legacyCoach.certifications 
      : legacyCoach.certifications 
        ? [legacyCoach.certifications] 
        : [],
    experience: 0, // Default value since LegacyCoach doesn't have experience
    bio: legacyCoach.notes || '',
    hourlyRate: legacyCoach.salary ? Number(legacyCoach.salary) / 2080 : undefined, // Convert annual salary to hourly rate
    maxClients: 20, // Default value
  };
}

/**
 * Transform frontend LegacyCoach data to backend Coach update format
 */
export function transformLegacyToUpdateData(legacyCoach: Partial<LegacyCoach>): UpdateCoachData {
  console.log('=== transformLegacyToUpdateData DEBUG ===');
  console.log('Input legacyCoach:', legacyCoach);
  console.log('Input specializations:', legacyCoach.specializations, 'Type:', typeof legacyCoach.specializations, 'IsArray:', Array.isArray(legacyCoach.specializations));
  console.log('Input certifications:', legacyCoach.certifications, 'Type:', typeof legacyCoach.certifications, 'IsArray:', Array.isArray(legacyCoach.certifications));

  const updateData: UpdateCoachData = {};

  // Basic fields - convert to strings
  if (legacyCoach.firstName !== undefined) updateData.firstName = String(legacyCoach.firstName);
  if (legacyCoach.lastName !== undefined) updateData.lastName = String(legacyCoach.lastName);
  if (legacyCoach.notes !== undefined) updateData.bio = String(legacyCoach.notes || '');
  
  // Array fields - backend expects arrays for both create and update operations
  if (legacyCoach.specializations !== undefined) {
    updateData.specializations = Array.isArray(legacyCoach.specializations) 
      ? legacyCoach.specializations.filter((s: string) => s && typeof s === 'string')
      : legacyCoach.specializations 
        ? [legacyCoach.specializations] 
        : [];
    console.log('Processed specializations:', updateData.specializations, 'Type:', typeof updateData.specializations, 'IsArray:', Array.isArray(updateData.specializations));
  }
  if (legacyCoach.certifications !== undefined) {
    updateData.certifications = Array.isArray(legacyCoach.certifications)
      ? legacyCoach.certifications.filter((c: string) => c && typeof c === 'string')
      : legacyCoach.certifications 
        ? [legacyCoach.certifications] 
        : [];
    console.log('Processed certifications:', updateData.certifications, 'Type:', typeof updateData.certifications, 'IsArray:', Array.isArray(updateData.certifications));
  }

  // Numeric fields
  if (legacyCoach.salary !== undefined) {
    // Convert annual salary to hourly rate (assuming 40 hours/week * 52 weeks/year = 2080 hours/year)
    updateData.hourlyRate = legacyCoach.salary / 2080;
  }

  console.log('Final updateData:', updateData);
  console.log('=== END transformLegacyToUpdateData DEBUG ===');
  return updateData;
}

// Separate function to extract phone number for user updates
export function extractPhoneNumberFromLegacy(legacyCoach: Partial<LegacyCoach>): string | undefined {
  return legacyCoach.phone !== undefined ? String(legacyCoach.phone || '') : undefined;
}

/**
 * Calculate performance metrics for legacy format from backend performance data
 */
export function transformPerformanceToLegacy(performance: CoachPerformance, coach: Coach): LegacyCoachPerformance {
  return {
    // Backend CoachPerformance fields (pass through)
    ...performance,
    
    // BaseCoachItem fields from coach
    id: coach.id,
    firstName: coach.user?.firstName || '',
    lastName: coach.user?.lastName || '',
    email: coach.user?.email || '',
    
    // Frontend computed fields from backend data
    totalClients: performance.clientCount,
    activeClients: performance.clientCount, // Assuming all are active
    retentionRate: performance.clientRetentionRate,
    sessionsCompleted: 0, // Not available in backend - placeholder
    avgSessionRating: performance.averageClientRating,
    responseTime: performance.avgClientOnboardingTime * 24, // Convert days to hours
    goalAchievementRate: performance.onboardingCompletionRate,
    newClientsThisMonth: 0, // Would need additional API call - placeholder
    clientsLostThisMonth: 0, // Would need additional API call - placeholder
    specializations: coach.specializations || [],
    performanceTrend: 'stable' as const, // Would need historical data
    lastActivity: coach.updatedAt ? coach.updatedAt.toString() : '',
  };
}

/**
 * Transform coach query options from legacy to backend format
 */
export function transformQueryOptions(legacyFilters: any) {
  const backendOptions: any = {};
  
  if (legacyFilters.searchText) backendOptions.search = legacyFilters.searchText;
  if (legacyFilters.specializations?.length > 0) {
    backendOptions.specialization = legacyFilters.specializations[0]; // Backend takes single specialization
  }
  if (legacyFilters.statuses?.length > 0) {
    // Map status to isActive
    const hasActive = legacyFilters.statuses.includes('Active') || legacyFilters.statuses.includes('Onboarding');
    const hasInactive = legacyFilters.statuses.includes('Inactive');
    
    if (hasActive && !hasInactive) {
      backendOptions.isActive = true;
    } else if (hasInactive && !hasActive) {
      backendOptions.isActive = false;
    }
    // If both or neither, don't filter by status
  }
  
  return backendOptions;
}

/**
 * Format coach name for display
 */
export function formatCoachName(coach: Coach | LegacyCoach): string {
  if ('user' in coach) {
    // Backend Coach format
    return `${coach.user.firstName || ''} ${coach.user.lastName || ''}`.trim();
  } else {
    // Legacy Coach format
    return `${coach.firstName} ${coach.lastName}`.trim();
  }
}

/**
 * Get coach email for display
 */
export function getCoachEmail(coach: Coach | LegacyCoach): string {
  if ('user' in coach) {
    // Backend Coach format
    return coach.user.email;
  } else {
    // Legacy Coach format
    return coach.email;
  }
}

/**
 * Get coach phone for display
 */
export function getCoachPhone(coach: Coach | LegacyCoach): string {
  if ('user' in coach) {
    // Backend Coach format
    return coach.user.phoneNumber || 'N/A';
  } else {
    // Legacy Coach format
    return coach.phone || 'N/A';
  }
}

/**
 * Get coach status for display
 */
export function getCoachStatus(coach: Coach): 'Active' | 'Inactive' | 'Onboarding' {
  if (!coach.isActive) return 'Inactive';
  
  const clientCount = coach._count?.assignedClients || coach.assignedClients?.length || 0;
  return clientCount > 0 ? 'Active' : 'Onboarding';
}

/**
 * Get coach assigned clients count
 */
export function getCoachClientCount(coach: Coach | LegacyCoach): number {
  if ('user' in coach) {
    // Backend Coach format
    return coach._count?.assignedClients || coach.assignedClients?.length || 0;
  } else {
    // Legacy Coach format
    return coach.assignedClients;
  }
}

/**
 * Transform backend CoachPerformance to ExtendedCoachPerformance for frontend components
 */
export function transformToExtendedPerformance(performance: CoachPerformance, coach: Coach): ExtendedCoachPerformance {
  return {
    // Backend CoachPerformance fields (pass through)
    ...performance,
    
    // BaseCoachItem fields from coach
    id: coach.id,
    firstName: coach.user?.firstName || '',
    lastName: coach.user?.lastName || '',
    email: coach.user?.email || '',
    
    // Frontend computed fields from backend data
    totalClients: performance.clientCount,
    activeClients: performance.clientCount, // Assuming all are active
    retentionRate: performance.clientRetentionRate,
    sessionsCompleted: 0, // Not available in backend - placeholder
    avgSessionRating: performance.averageClientRating,
    responseTime: performance.avgClientOnboardingTime * 24, // Convert days to hours
    goalAchievementRate: performance.onboardingCompletionRate,
    newClientsThisMonth: 0, // Not available - placeholder
    clientsLostThisMonth: 0, // Not available - placeholder
    specializations: coach.specializations || [],
    performanceTrend: 'stable' as const, // Would need historical data
    lastActivity: coach.updatedAt ? coach.updatedAt.toString() : '',
  };
} 